/**
 * Distributors Component
 * Manages distributors with proper API integration
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { distributorService } from '../../services/distributorService';
import { useRBAC } from '../../hooks/useRBAC';
import { getQuickFilters } from '../../utils/filterUtils';
import ProfessionalLoader from "../common/ProfessionalLoader";
import DistributorForm from './DistributorForm';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedStats from '../common/UnifiedStats';
import UnifiedFilters from '../common/UnifiedFilters';
import UnifiedActionPanel from '../common/UnifiedActionPanel';
import UnifiedTable from '../common/UnifiedTable';
import UnifiedEmptyState from '../common/UnifiedEmptyState';
import './Distributors.css';

const Distributors = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageIntegrations } = useRBAC();

  // State management
  const [distributors, setDistributors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingDistributor, setEditingDistributor] = useState(null);
  const [selectedDistributors, setSelectedDistributors] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [searchParams, setSearchParams] = useState({
    search: '',
    is_active: '',
    status: ''
  });
  const [searchInput, setSearchInput] = useState('');

  // Statistics
  const [stats, setStats] = useState({
    totalDistributors: 0,
    activeDistributors: 0,
    inactiveDistributors: 0
  });



  // Load distributors
  const loadDistributors = useCallback(async (isSearch = false, filters = {}) => {
    try {
      // Only show loading spinner on initial load, not during search
      if (!isSearch) {
        setIsLoading(true);
      }

      // Use search parameters if provided, otherwise use current searchParams
      const searchFilters = Object.keys(filters).length > 0 ? filters : searchParams;

      // Try server-side filtering first
      const response = await distributorService.getDistributors(searchFilters);

      if (response.success) {
        const distributorsData = response.data || [];
        setDistributors(distributorsData);
        updateStatistics(distributorsData);
      } else {
        showError(response.message || 'Failed to load distributors', 'Error');
        setDistributors([]);
      }
    } catch (error) {
      showError('Failed to load distributors', 'Error');
      setDistributors([]);
    } finally {
      if (!isSearch) {
        setIsLoading(false);
      }
    }
  }, [showError, searchParams]);

  // Client-side filtering function with improved null checking
  const filterDistributors = (distributorsData, filters) => {
    let filtered = [...distributorsData];

    // Filter by search term with proper null checking
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter(distributor => {
        const searchableFields = [
          distributor.name,
          distributor.contact_person,
          distributor.email,
          distributor.phone
        ].filter(Boolean); // Remove null/undefined values

        return searchableFields.some(field =>
          field.toLowerCase().includes(searchTerm)
        );
      });
    }

    // Filter by active status
    if (filters.is_active === 'true') {
      filtered = filtered.filter(distributor => distributor.is_active === true);
    } else if (filters.is_active === 'false') {
      filtered = filtered.filter(distributor => distributor.is_active === false);
    }

    // Filter by status (if needed)
    if (filters.status && filters.status !== '') {
      filtered = filtered.filter(distributor => distributor.status === filters.status);
    }

    return filtered;
  };

  // Computed filtered distributors - single source of truth
  const filteredDistributors = useMemo(() => {
    return filterDistributors(distributors, searchParams);
  }, [distributors, searchParams]);

  // Update statistics
  const updateStatistics = (distributorsData) => {
    const newStats = {
      totalDistributors: distributorsData.length,
      activeDistributors: distributorsData.filter(distributor => distributor.is_active).length,
      inactiveDistributors: distributorsData.filter(distributor => !distributor.is_active).length
    };
    setStats(newStats);
  };


  // Load distributors on component mount
  useEffect(() => {
    loadDistributors();
  }, [loadDistributors]);

  useEffect(() => {
    setSearchInput(searchParams.search || '');
  }, [searchParams.search]);

  // Search runs only on submit (search icon or Enter), matching Applications
  const handleSearchInputChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const handleSearch = useCallback(() => {
    setSearchParams(prev => ({ ...prev, search: searchInput }));
  }, [searchInput]);

  // Apply quick filters with toggle functionality
  const applyQuickFilter = (filterType) => {
    setSearchParams(prev => {
      const newParams = { ...prev };

      switch (filterType) {
        case 'active':
          if (prev.is_active === 'true') {
            newParams.is_active = '';
          } else {
            newParams.is_active = 'true';
          }
          break;
        case 'inactive':
          if (prev.is_active === 'false') {
            newParams.is_active = '';
          } else {
            newParams.is_active = 'false';
          }
          break;
        case 'all':
          newParams.search = '';
          newParams.is_active = '';
          newParams.status = '';
          break;
        default:
          break;
      }

      return newParams;
    });
  };

  // Handle create distributor
  const handleCreateDistributor = async (distributorData) => {
    setIsProcessing(true);
    try {
      const response = await distributorService.createDistributor(distributorData);

      if (response.success) {
        showSuccess('Distributor created successfully', 'Success');
        setShowForm(false);
        loadDistributors();
      } else {
        showError(response.message || 'Failed to create distributor', 'Error');
      }
    } catch (error) {
      showError('Failed to create distributor', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle update distributor
  const handleUpdateDistributor = async (distributorId, distributorData) => {
    setIsProcessing(true);
    try {
      const response = await distributorService.updateDistributor(distributorId, distributorData);

      if (response.success) {
        showSuccess('Distributor updated successfully', 'Success');
        setEditingDistributor(null);
        loadDistributors();
      } else {
        showError(response.message || 'Failed to update distributor', 'Error');
      }
    } catch (error) {
      showError('Failed to update distributor', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };



  // Handle distributor selection
  const handleDistributorSelect = (distributorId) => {
    setSelectedDistributors(prev =>
      prev.includes(distributorId)
        ? prev.filter(id => id !== distributorId)
        : [...prev, distributorId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDistributors.length === filteredDistributors.length) {
      setSelectedDistributors([]);
    } else {
      setSelectedDistributors(filteredDistributors.map(distributor => distributor.id));
    }
  };

  // Handle delete selected distributors
  const handleDeleteSelected = async () => {
    if (selectedDistributors.length === 0) return;

    const confirmMessage = selectedDistributors.length === 1
      ? 'Are you sure you want to delete this distributor?'
      : `Are you sure you want to delete ${selectedDistributors.length} distributors?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsProcessing(true);
    try {
      const deletePromises = selectedDistributors.map(id =>
        distributorService.deleteDistributor(id)
      );

      const results = await Promise.all(deletePromises);
      const failedDeletes = results.filter(result => !result.success);

      if (failedDeletes.length === 0) {
        const message = selectedDistributors.length === 1
          ? 'Distributor deleted successfully'
          : `${selectedDistributors.length} distributors deleted successfully`;
        showSuccess(message, 'Success');
        setSelectedDistributors([]);
        loadDistributors();
      } else {
        showError(`Failed to delete ${failedDeletes.length} distributors`, 'Error');
      }
    } catch (error) {
      showError('Failed to delete distributors', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle distributor action (single or bulk)
  const handleDistributorAction = async () => {
    if (!selectedAction || selectedDistributors.length === 0) {
      showError('Please select an action and at least one distributor');
      return;
    }

    try {
      setIsProcessing(true);

      // Handle view and edit actions (single distributor only)
      if (selectedAction === 'view' || selectedAction === 'edit') {
        if (selectedDistributors.length > 1) {
          showError('View and Edit actions can only be performed on one distributor at a time');
          return;
        }

        const distributorId = selectedDistributors[0];
        const distributor = filteredDistributors.find(d => d.id === distributorId);

        if (selectedAction === 'edit') {
          setEditingDistributor(distributor);
          setShowForm(true);
        }

        setSelectedDistributors([]);
        setSelectedAction('');
        setActionReason('');
        return;
      }

      // Handle bulk actions
      let response;
      let successMessage;

      if (selectedDistributors.length === 1) {
        // Single distributor action
        const distributorId = selectedDistributors[0];

        switch (selectedAction) {
          case 'delete':
            response = await distributorService.deleteDistributor(distributorId);
            successMessage = 'Distributor deleted successfully';
            break;
          default:
            showError('Action not implemented yet');
            return;
        }
      } else {
        // Bulk distributor action
        switch (selectedAction) {
          case 'delete':
            const deletePromises = selectedDistributors.map(id =>
              distributorService.deleteDistributor(id)
            );
            const results = await Promise.all(deletePromises);
            const failedDeletes = results.filter(result => !result.success);

            if (failedDeletes.length === 0) {
              successMessage = `${selectedDistributors.length} distributors deleted successfully`;
            } else {
              showError(`Failed to delete ${failedDeletes.length} distributors`);
              return;
            }
            break;
          default:
            showError('Action not implemented yet');
            return;
        }
      }

      if (response && response.success) {
        showSuccess(response.message || successMessage);
        await loadDistributors();
      } else if (response && !response.success) {
        showError(response.message || 'Action failed');
      } else {
        showSuccess(successMessage);
        await loadDistributors();
      }

      setSelectedDistributors([]);
      setSelectedAction('');
      setActionReason('');
    } catch (error) {
      showError('Failed to execute action');
    } finally {
      setIsProcessing(false);
    }
  };


  if (isLoading) {
    return (
      <div className="distributors">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="distributors">
      {/* Distributors Management Section */}
      <div className="distributors-management-container">
        {/* Unified Header */}
        <UnifiedHeader
          title="Distributors"
          subtitle="Manage film distributors and their contact information"
          icon="🎬"
          count={stats.totalDistributors}
          countLabel="Total Distributors"
          selectedCount={selectedDistributors.length}
          selectedLabel="Selected"
        >
          {canManageIntegrations && (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
              disabled={isProcessing}
            >
              ➕ Create Distributor
            </button>
          )}
        </UnifiedHeader>

        {/* Unified Statistics */}
        <UnifiedStats
          stats={[
            { title: '🎬 Total Distributors', value: stats.totalDistributors },
            { title: '✅ Active Distributors', value: stats.activeDistributors },
            { title: '❌ Inactive Distributors', value: stats.inactiveDistributors }
          ]}
        />

        {/* Unified Filters */}
        <UnifiedFilters
          searchValue={searchInput}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearch}
          quickFilters={getQuickFilters('distributors', searchParams)}
          onQuickFilterClick={applyQuickFilter}
        />

        {/* Unified Action Panel */}
        <UnifiedActionPanel
          selectedCount={selectedDistributors.length}
          totalCount={filteredDistributors.length}
          selectedItemsLabel="distributors"
          onSelectAll={handleSelectAll}
          onClearSelection={() => setSelectedDistributors([])}
          selectedAction={selectedAction}
          onActionChange={setSelectedAction}
          onExecuteAction={handleDistributorAction}
          isProcessing={isProcessing}
          actionOptions={[
            { value: 'edit', label: '✏️ Edit Distributor', disabled: false },
            { value: 'delete', label: '🗑️ Delete Distributors', disabled: false }
          ]}
        />

        {/* Distributors Table */}
        {filteredDistributors.length > 0 ? (
          <UnifiedTable
            columns={[
              { key: 'id', header: 'ID', className: 'unified-table-select-header' },
              { key: 'name', header: 'Name' },
              { key: 'contact_person', header: 'Contact Person' },
              { key: 'email', header: 'Email' },
              { key: 'phone', header: 'Phone' },
              { key: 'status', header: 'Status' }
            ]}
            data={filteredDistributors}
            onRowClick={(distributor) => {
              // Handle row click - could open edit modal or view details
              console.log('Distributor clicked:', distributor);
            }}
            onSelectRow={handleDistributorSelect}
            selectedRows={selectedDistributors}
            renderCell={(column, distributor) => {
              switch (column.key) {
                case 'id':
                  return <span className="unified-distributor-id">{distributor.id}</span>;
                case 'name':
                  return <span className="unified-distributor-name">{distributor.name}</span>;
                case 'contact_person':
                  return <span className="unified-contact-person">{distributor.contact_person || 'N/A'}</span>;
                case 'email':
                  return distributor.email ? (
                    <a href={`mailto:${distributor.email}`} className="unified-email-link">
                      {distributor.email}
                    </a>
                  ) : (
                    <span className="unified-no-data">N/A</span>
                  );
                case 'phone':
                  return distributor.phone ? (
                    <a href={`tel:${distributor.phone}`} className="unified-phone-link">
                      {distributor.phone}
                    </a>
                  ) : (
                    <span className="unified-no-data">N/A</span>
                  );
                case 'status':
                  return (
                    <span className={`unified-status-badge ${distributor.is_active ? 'active' : 'inactive'}`}>
                      {distributor.is_active ? 'Active' : 'Inactive'}
                    </span>
                  );
                default:
                  return distributor[column.key];
              }
            }}
          />
        ) : (
          <UnifiedEmptyState
            icon="🎬"
            title="No distributors found"
            description="Try adjusting your search criteria or create a new distributor."
            actions={[
              ...(canManageIntegrations ? [{
                label: 'Create First Distributor',
                icon: '➕',
                onClick: () => setShowForm(true),
                variant: 'primary'
              }] : [])
            ]}
          />
        )}
      </div>

      {/* Distributor Form Modal */}
      {showForm && (
        <DistributorForm
          distributor={editingDistributor}
          onSubmit={editingDistributor ? handleUpdateDistributor : handleCreateDistributor}
          onClose={() => {
            setShowForm(false);
            setEditingDistributor(null);
          }}
          isSubmitting={isProcessing}
          existingDistributors={distributors}
        />
      )}
    </div>
  );
};

export default Distributors;
